import React, {useMemo, useState} from "react";
import {useIntl} from "react-intl";
import {useQuery} from "@tanstack/react-query";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {Table, TableData} from "peggirkit";
import {DeliveryArea} from "@/data/entities/deliveryArea";
import DeleteDeliveryAreaModal, {DeleteDeliveryAreaState} from "@widgets/deliveryAreas/delete/DeleteDeliveryAreaModal";
import {getDeliveryAreasTableColumns} from "@widgets/deliveryAreas/overview/table/DeliveryAreasTableColumns";
import EditDeliveryAreaModal, {EditDeliveryAreaState} from "@widgets/deliveryAreas/edit/EditDeliveryAreaModal";
import {SetStateFn} from "@/utils/SetStateUtils";
import {api} from "@/data/api";

type Props = {
    editModal: EditDeliveryAreaState,
    setEditModal: SetStateFn<EditDeliveryAreaState>,
};

const DeliveryAreasTable = ({editModal, setEditModal}: Props) => {
    const intl = useIntl();
    const {dashboardLocation} = useRestaurant();
    const [deleteModal, setDeleteModal] = useState<DeleteDeliveryAreaState>({showModal: false, deliveryArea: null});
    const [tableData, setTableData] = useState<TableData<DeliveryArea>[]>([]);
    const {isFetching} = useQuery({
        queryKey: ["delivery-areas", dashboardLocation.id],
        queryFn: async () => {
            const result: DeliveryArea[] = await api.deliveryArea.listDeliveryAreas(dashboardLocation.id);
            setTableData(result);
            return result;
        },
    });
    const columns = useMemo(
        () => getDeliveryAreasTableColumns(intl, setDeleteModal, setEditModal),
        [intl, setDeleteModal, setEditModal]);

    return (
        <div className={"flex flex-col gap-4"}>
            <Table<DeliveryArea>
                columns={columns}
                data={tableData}
                setData={setTableData}
                loading={isFetching}
                loadingConfig={{
                    skeletonRepeat: 20,
                }}
                rowConfig={{
                    hoverEffect: true,
                }}
            />

            <DeleteDeliveryAreaModal
                show={deleteModal.showModal}
                setShow={(show: boolean) => setDeleteModal(prev => ({...prev, showModal: show}))}
                deliveryArea={deleteModal.deliveryArea}
                setTableData={setTableData}
            />

            <EditDeliveryAreaModal
                show={editModal.showModal}
                setShow={(show: boolean) => setEditModal(prev => ({...prev, showModal: show}))}
                deliveryArea={editModal.deliveryArea}
                setTableData={setTableData}
            />
        </div>
    );
};

export default DeliveryAreasTable;